import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import qmrsGroupedByDfmCategoryQuery from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/qmrsGroupedByDfmCategoryQuery'
import { useDispatch } from 'react-redux'
import RequestChangeModal from '@Builder/QuoteDetailPage/RequestChangeModal'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import Button from '~/core/Button'
import { REQUESTED, PURCHASED } from '~/lib/quoteStatuses'
import UploadQuoteModelRevisionModal from '~/apps/consumer/components/UploadQuoteModelRevisionModal'
import {
    getCoreQuoteDetailsData,
    getLegacyQuoteDetailsData,
} from '~/redux-modules/query-refactor/actions'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import ConfirmationModal from './ConfirmationModal'
import qmrRevisionRefetchQuery from './qmrRevisionRefetchQuery'

type UploadRevisionButtonPropsType = {
    isImQuoteType?: boolean
    isLocatedInImDfmModal: boolean
    isPreviousQmrRevision?: boolean
    placement: string
    qmrId: string
    refetch?: () => void
    isQuoteLocked?: boolean | null | undefined
}

const UploadRevisionButton = (props: UploadRevisionButtonPropsType) => {
    const {
        isImQuoteType,
        isLocatedInImDfmModal,
        isPreviousQmrRevision,
        placement,
        qmrId,
        refetch,
        isQuoteLocked,
    } = props
    const isCpqEnabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V1,
    )
    const dispatch = useDispatch()
    const [showUploadRevisionModal, setShowUploadRevisionModal] =
        useState<boolean>(false)
    const [showRequestChangeModal, setShowRequestChangeModal] =
        useState<boolean>(false)
    const { orderName } = useParams<{
        orderName: string
    }>()

    const isOdp = Boolean(orderName)

    const { data } = useQuery(qmrRevisionRefetchQuery, {
        variables: { id: qmrId },
        fetchPolicy: 'cache-first',
    })

    const setUploadRevisionModalVisibility = (value: boolean) => {
        return setShowUploadRevisionModal(value)
    }

    const qmrRevisionToUpload = data?.quoteModelRevision
    const quote = qmrRevisionToUpload?.quoteModel?.quote
    const quoteId = quote?.id
    const quoteStatus = quote?.status

    const disableEditing =
        quoteStatus === REQUESTED ||
        quoteStatus === PURCHASED ||
        isPreviousQmrRevision

    const refetchQueries = [
        {
            query: qmrRevisionRefetchQuery,
            variables: {
                id: qmrId,
            },
        },
        {
            query: qmrsGroupedByDfmCategoryQuery,
            variables: {
                quoteId,
            },
        },
    ]

    const manualRefetch = (id: string) => {
        dispatch(getLegacyQuoteDetailsData(id))
        dispatch(getCoreQuoteDetailsData(id))
    }

    return (
        <>
            <Button
                data-test-target="upload-part-revision"
                disabled={disableEditing}
                name="upload-revision"
                onClick={() =>
                    isQuoteLocked && isCpqEnabled
                        ? setShowRequestChangeModal(true)
                        : setUploadRevisionModalVisibility(true)
                }
                placement={placement}
                type={isLocatedInImDfmModal ? 'primary' : undefined}
            >
                Upload revision
            </Button>
            <RequestChangeModal
                isVisible={showRequestChangeModal}
                quoteId={quoteId}
                closeModal={() => setShowRequestChangeModal(false)}
                uploadRevision={true}
            />
            {showUploadRevisionModal && !isOdp && (
                <UploadQuoteModelRevisionModal
                    isImQuote={isImQuoteType}
                    quoteId={quoteId}
                    quoteModelRevision={qmrRevisionToUpload}
                    refetchQuery={refetchQueries}
                    setUploadRevisionModalVisibility={
                        setUploadRevisionModalVisibility
                    }
                    visible={showUploadRevisionModal}
                    manualRefetch={manualRefetch}
                    refetch={refetch}
                />
            )}
            {showUploadRevisionModal && isOdp && (
                <ConfirmationModal
                    showUploadRevisionModal={showUploadRevisionModal}
                    onClose={() => setUploadRevisionModalVisibility(false)}
                />
            )}
        </>
    )
}

export default UploadRevisionButton
